import { useMemo } from 'react';
import type { ComponentType, SVGProps } from 'react';
import type { AgentType } from '@/lib/types';
import { usePlugins } from '@/hooks/config/usePlugins';
import type { ResolvedAgentType } from '@/lib/plugin-registry';

export interface ModelOption {
  id: string;
  name: string;
  default?: boolean;
  /**
   * Provider id for grouping in the short model list. Matrix discovery always
   * supplies this; registry-only paths may omit it and fall back to a
   * `provider/model` id prefix.
   */
  providerId?: string;
  /**
   * When true, the launch picker shows this model in the short default list.
   * Hand-maintained curated / alias / gateway entries set this.
   */
  recommended?: boolean;
  /**
   * Whether this model accepts image input. Omitted means "capable" — only
   * models that reject images set this to `false` (e.g. OpenCode's free
   * text-only tier). Used to gate image attachments in the composer.
   */
  supportsImages?: boolean;
  contextWindowTokens?: number;
}

export interface AgentTypeInfo {
  type: AgentType;
  name: string;
  vendor: string;
  tagline: string;
  description: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Optional vendor/company icon shown alongside the vendor name. */
  vendorIcon?: ComponentType<SVGProps<SVGSVGElement>>;
  capabilities: string[];
  authRequirements: { name: string; required: boolean; description: string }[];
  models: ModelOption[];
  reportsContextUsage?: boolean;
  /**
   * When true, messages sent while a turn is running are delivered to the
   * provider immediately (steering the in-flight turn) instead of queued.
   */
  supportsSteering?: boolean;
  /** When true, a running agent's composer offers a model picker. Display gate only. */
  supportsRuntimeModelSwitch?: boolean;
  defaultContextWindowTokens?: number;
  reasoningOptions?: ModelOption[];
  reasoningEnvVar?: string;
  docsUrl?: string;
  color: string;
}

export class AgentTypeRegistry {
  private types: Map<AgentType, AgentTypeInfo>;

  constructor(entries: AgentTypeInfo[]) {
    this.types = new Map(entries.map((e) => [e.type, e]));
  }

  get(type: AgentType): AgentTypeInfo | undefined {
    return this.types.get(type);
  }

  require(type: AgentType): AgentTypeInfo {
    const info = this.types.get(type);
    if (!info) throw new Error(`Unknown agent type: ${type}`);
    return info;
  }

  getAll(): AgentTypeInfo[] {
    return [...this.types.values()];
  }

  getDefaultModel(type: AgentType): string | undefined {
    const info = this.types.get(type);
    if (!info) return undefined;
    return info.models.find((m) => m.default)?.id ?? info.models[0]?.id;
  }

  /**
   * Whether the given model accepts image input. Defaults to `true` for any
   * model that does not explicitly opt out (`supportsImages: false`), and for
   * unknown agent types / models, so image attachments stay enabled unless a
   * model is known to reject them.
   */
  modelSupportsImages(type: AgentType, modelId: string | null | undefined): boolean {
    if (!modelId) return true;
    const model = this.types.get(type)?.models.find((m) => m.id === modelId);
    return model?.supportsImages !== false;
  }

  /**
   * Context window, in tokens, to size a usage meter against. Prefers the
   * model the stream reports as answering: a harness `/model` switch can move
   * a session onto a model with a different window, and the meter has to
   * follow it or it shows a percentage of the wrong denominator right beside a
   * chip naming the new model.
   *
   * Falls back to the launch-time model when the stream reports an id the
   * registry does not list — a CLI alias, or a dated id against a short entry
   * — so an unrecognized name never costs us the window we already knew.
   * `undefined` when neither is known and the agent type declares no default.
   */
  contextWindowTokens(
    type: AgentType,
    activeModelId: string | null | undefined,
    launchModelId: string | null | undefined,
  ): number | undefined {
    const info = this.types.get(type);
    if (!info) return undefined;
    const model =
      (activeModelId ? info.models.find((m) => m.id === activeModelId) : undefined) ??
      (launchModelId ? info.models.find((m) => m.id === launchModelId) : undefined);
    return model?.contextWindowTokens ?? info.defaultContextWindowTokens;
  }
}

function resolvedToInfo(resolved: ResolvedAgentType): AgentTypeInfo {
  return {
    ...resolved,
    icon: resolved.icon as ComponentType<SVGProps<SVGSVGElement>>,
    vendorIcon: resolved.vendorIcon as ComponentType<SVGProps<SVGSVGElement>> | undefined,
  };
}

/**
 * Returns an `AgentTypeRegistry` populated from plugin manifests. The registry
 * is empty until manifests resolve, which is the source of truth for which
 * agent providers exist, so callers that read it during the brief boot window
 * see no types rather than a stale hardcoded list.
 */
export function useAgentTypeRegistry(): AgentTypeRegistry {
  const { agentTypes } = usePlugins();
  return useMemo(() => new AgentTypeRegistry(agentTypes.map(resolvedToInfo)), [agentTypes]);
}
